/**
 * What a sales order line can BE, per product instance.
 *
 * ⚠ **This MIRRORS `api/src/sales/item-types.js`. If either side changes, change
 * both.** Same arrangement as `instance.ts` ↔ `api/src/config.js`, and tolerable
 * for the same reason: drift announces itself immediately. The API validates
 * every line against its own list, so an item this file offers and the API does
 * not know returns a 400 the first time anyone picks it. There is no silent-wrong
 * state to discover months later.
 *
 * (The alternative is serving the catalog over HTTP the way the fulfillment
 * template already is, which would delete the duplication at the cost of a fetch
 * and a loading state before the dropdown can render. Cheap to switch to later if
 * the mirroring ever actually bites.)
 *
 * Level 2 on the mechanism ladder in `docs/operations/two-instances.md`: same
 * feature, different data, no `if (isSwarm)` anywhere downstream.
 *
 * ── FIELDS ──────────────────────────────────────────────────────────────────
 * `value`     The string stored in `sales_order_lines.item_type`.
 * `label`     Singular. Dropdown options and the line-item table.
 * `plural`    Summary rollups: "50 Drones".
 * `kind`      `stock` | `service` | `charge`, matching
 *             `purchase_order_lines.line_type`. Only `stock` reaches the
 *             fulfillment checklist (enforced on the API side).
 * `group`     Optional. Types that collapse into one summary row. Firefleet's two
 *             drone lines report as a single "Drones" figure.
 * `emphasis`  Optional. Renders that summary row as the order's headline number.
 * `variant`   Optional. The build this item type IS (mirrors the API): Drone Standard is
 *             `standard`, Drone Pyro is `pyro`. A line of such a type never asks for a Variant.
 * `product`   Optional (mirrors the API). `model`: the line names a drone model, and the
 *             dropdown offers one choice per active model. `none`: no product. Unset: a
 *             free Product picker.
 */

import { INSTANCE } from '../instance';
import type { Instance } from '../instance';

export type OrderStat = 'drones' | 'batteries' | 'chargers' | 'ground';

export interface ItemType {
  value: string;
  label: string;
  plural: string;
  kind: 'stock' | 'service' | 'charge';
  group?: string;
  emphasis?: boolean;
  variant?: string;
  product?: 'model' | 'none';
  /** Which orders-page stats column counts this item. Unset: no column. */
  stat?: OrderStat;
}

const CATALOG: Record<Instance, ItemType[]> = {
  // Firefly Drone Shows. Unchanged vocabulary, relocated from three places.
  // The em-dashes below are pre-existing copy, not new: they break the
  // no-em-dash house rule and deserve a pass of their own, but editing them
  // here would have hidden a copy change inside a mechanical refactor.
  firefly: [
    { value: 'drone_ls',       label: 'Drone — Standard',  plural: 'LS Drones',       kind: 'stock', group: 'Drones', emphasis: true, variant: 'standard', stat: 'drones' },
    { value: 'drone_pyro',     label: 'Drone — Pyro',      plural: 'Pyro Drones',     kind: 'stock', group: 'Drones', emphasis: true, variant: 'pyro', stat: 'drones' },
    { value: 'battery',        label: 'Battery',                plural: 'Batteries',       kind: 'stock', stat: 'batteries' },
    { value: 'charger',        label: 'Charger',                plural: 'Chargers',        kind: 'stock', stat: 'chargers' },
    { value: 'ground_station', label: 'Ground Station and RTK', plural: 'Ground Stations', kind: 'stock', stat: 'ground' },
    { value: 'gear_pelican',   label: 'Gear Pelican',           plural: 'Gear Pelicans',   kind: 'stock', stat: 'ground' },
    { value: 'drone_carrier',  label: 'Drone Carrier',          plural: 'Drone Carriers',  kind: 'stock', stat: 'ground' },
  ],

  // Swarm Defense. From the priced customer quote and the government CLIN
  // schedule supplied 2026-08-03, in quote order: platform, software, spares,
  // add-ons, support, freight. Doc SKUs (SDT G2, G2-CARBON-ARM, ...) live in the
  // API copy of this catalog as comments; there is no SKU column to hold them.
  // `drone` is every drone model at once; the line's product says which.
  swarm: [
    { value: 'drone',             label: 'Drone',                            plural: 'Drones',                  kind: 'stock',   emphasis: true, product: 'model', stat: 'drones' },
    { value: 'gcs_kit',           label: 'Ground Control Station Kit',       plural: 'Ground Control Stations', kind: 'stock',   product: 'none', stat: 'ground' },
    { value: 'access_point_kit',  label: 'Extended Range Access Point Kit',  plural: 'Access Point Kits',       kind: 'stock',   product: 'none', stat: 'ground' },
    { value: 'license_annual',    label: 'Swarm Studio License (Annual)',    plural: 'Annual Licenses',         kind: 'service', product: 'none' },
    { value: 'license_perpetual', label: 'Swarm Studio License (Perpetual)', plural: 'Perpetual Licenses',      kind: 'service', product: 'none' },
    { value: 'battery',           label: 'Battery',                          plural: 'Batteries',               kind: 'stock',   product: 'none', stat: 'batteries' },
    { value: 'propeller_cw',      label: 'CW Propeller',                     plural: 'CW Propellers',           kind: 'stock',   product: 'none' },
    { value: 'propeller_ccw',     label: 'CCW Propeller',                    plural: 'CCW Propellers',          kind: 'stock',   product: 'none' },
    { value: 'carbon_arm',        label: 'Carbon Arm',                       plural: 'Carbon Arms',             kind: 'stock',   product: 'none' },
    { value: 'motor',             label: 'Motor',                            plural: 'Motors',                  kind: 'stock',   product: 'none' },
    { value: 'drop_mechanism',    label: 'Drop Mechanism',                   plural: 'Drop Mechanisms',         kind: 'stock',   product: 'none' },
    { value: 'fpv_kit',           label: 'FPV Kit',                          plural: 'FPV Kits',                kind: 'stock',   product: 'none', stat: 'ground' },
    { value: 'fpv_monitor',       label: 'FPV Monitor Rx',                   plural: 'FPV Monitors',            kind: 'stock',   product: 'none', stat: 'ground' },
    { value: 'lipo_charger',      label: 'Basic Lipo Charger',               plural: 'Lipo Chargers',           kind: 'stock',   product: 'none', stat: 'chargers' },
    { value: 'shipping',          label: 'Shipping',                         plural: 'Shipping',                kind: 'charge',  product: 'none' },
  ],
};

/** Exported for tests that need to assert on the other instance's vocabulary. */
export const itemTypesFor = (inst: Instance): ItemType[] => CATALOG[inst] || CATALOG.firefly;

/** This build's item types, in display order. */
export const ITEM_TYPES: ItemType[] = itemTypesFor(INSTANCE);

/** The build an item type fixes, or null when the line chooses its own. */
export const variantOfItemType = (itemType: string): string | null =>
  ITEM_TYPES.find((t) => t.value === itemType)?.variant ?? null;

/** What the line's product may be: 'model', 'none', or null for a free choice. */
export const productOfItemType = (itemType: string): 'model' | 'none' | null =>
  ITEM_TYPES.find((t) => t.value === itemType)?.product ?? null;

/** Instance-parameterised for tests; app code uses `lineLabel`. A drone line reads by its model. */
export function lineLabelFor(inst: Instance, itemType: string, productName?: string | null): string {
  const type = itemTypesFor(inst).find((t) => t.value === itemType);
  if (type?.product === 'model' && productName) return productName;
  return type?.label || itemType;
}

export const lineLabel = (itemType: string, productName?: string | null): string =>
  lineLabelFor(INSTANCE, itemType, productName);

export interface SummaryRow {
  label: string;
  total: number;
  emphasis: boolean;
}

/**
 * Instance-parameterised, for tests. Prefer `summaryRows` in app code — same
 * arrangement as `allowedOn` / `routeAllowedOnInstance` in
 * `navigation/routeInstances.ts`, and for the same reason: `INSTANCE` is baked in
 * at build time, so a function that reads it directly can only ever be tested on
 * one product.
 */
export function summaryRowsFor(
  inst: Instance,
  lines: Array<{ item_type: string; quantity: number }>,
): SummaryRow[] {
  const rows: SummaryRow[] = [];
  const indexByLabel = new Map<string, number>();

  for (const type of itemTypesFor(inst)) {
    if (type.kind === 'charge') continue;

    const total = lines
      .filter((l) => l.item_type === type.value)
      .reduce((sum, l) => sum + (l.quantity || 0), 0);
    if (total === 0) continue;

    const label = type.group || type.plural;
    const existing = indexByLabel.get(label);
    if (existing !== undefined) {
      rows[existing].total += total;
      rows[existing].emphasis = rows[existing].emphasis || !!type.emphasis;
    } else {
      indexByLabel.set(label, rows.length);
      rows.push({ label, total, emphasis: !!type.emphasis });
    }
  }

  return rows;
}

/**
 * Roll an order's lines up into the figures worth putting on a summary card:
 * catalog order, grouped types combined, zeros omitted.
 *
 * `charge` lines are left out on purpose. "Shipping: 1" is not a count of
 * anything, and a summary that includes it reads like a bug.
 */
export const summaryRows = (
  lines: Array<{ item_type: string; quantity: number }>,
): SummaryRow[] => summaryRowsFor(INSTANCE, lines);
